import React, { useState } from "react";
import TimezoneSelect from "@/components/TimezoneSelect";
import { browserTimezone } from "@/lib/timezones";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Save, Trash2, Megaphone, Plus, Play, Flag, Archive } from "lucide-react";

const ROLES = [{ id: "host", label: "Tournament Host" }, { id: "referee", label: "Referees" }, { id: "captain", label: "Team Captains" }];
const numberInput = (value) => value === "" ? "" : parseInt(value, 10);

export default function ManageSettings({ data, refresh, setupMode }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const { tournament: t, announcements, sponsors } = data;
  const [form, setForm] = useState({
    name: t.name, description: t.description, location: t.location,
    timezone: t.timezone || browserTimezone(),
    logo_url: t.logo_url,
    max_teams: t.max_teams, min_members: t.min_members, max_members: t.max_members,
    num_fields: (t.fields || []).length || t.num_fields, match_duration: t.match_duration, break_time: t.break_time,
    score_entry: t.score_entry || ["host", "referee"],
    allow_captain_team_rename: !!t.allow_captain_team_rename,
    allow_captain_logo_change: !!t.allow_captain_logo_change,
    is_public: t.is_public !== false,
    type_config: t.type_config || {},
  });
  const [busy, setBusy] = useState(false);
  const [ann, setAnn] = useState({ title: "", body: "" });
  const [spo, setSpo] = useState({ name: "", logo_url: null, url: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleRole = (id) => {
    const cur = form.score_entry;
    set("score_entry", cur.includes(id) ? cur.filter((r) => r !== id) : [...cur, id]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.timezone) delete payload.timezone;
      await api.put(`/tournaments/${t.id}`, payload); toast.success("Settings saved"); refresh();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/tournaments/${t.id}/status`, { status });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (e) {
      if (e.response?.status === 402) {
        toast.message("Payment required before activation");
        const { data } = await api.post(`/tournaments/${t.id}/checkout`);
        if (data.url) window.location.href = data.url;
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const del = async () => { await api.delete(`/tournaments/${t.id}`); toast.success("Tournament deleted"); nav("/host"); };

  const addAnn = async () => { if (!ann.title.trim()) return; await api.post(`/tournaments/${t.id}/announcements`, ann); setAnn({ title: "", body: "" }); toast.success("Announcement posted"); refresh(); };
  const delAnn = async (id) => { await api.delete(`/announcements/${id}`); refresh(); };
  const addSpo = async () => { if (!spo.name.trim()) return; await api.post(`/tournaments/${t.id}/sponsors`, spo); setSpo({ name: "", logo_url: null, url: "" }); toast.success("Sponsor added"); refresh(); };
  const delSpo = async (id) => { await api.delete(`/sponsors/${id}`); refresh(); };

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h2 className="font-heading text-2xl font-bold">{setupMode ? "Setup" : "Settings"}</h2>

      <Card className="space-y-4 p-6">
        <h3 className="font-heading font-bold">Basic information</h3>
        <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="settings-name" /></div>
        <TimezoneSelect value={form.timezone} onChange={(value) => set("timezone", value)} />
        <p className="text-xs text-muted-foreground">Changing timezone changes the time display, not already scheduled game instants. Use Auto-schedule to rebuild game times from the tournament start time in the selected timezone. Older tournaments keep their existing behavior until a timezone is selected.</p>
        <div><Label>Location</Label><Input value={form.location} onChange={(e) => set("location", e.target.value)} className="mt-1.5" /></div>
        <div><Label>Description</Label><Textarea value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="mt-1.5" /></div>
        <div><Label>Logo</Label><div className="mt-1.5"><ImageUpload value={form.logo_url} onChange={(v) => set("logo_url", v)} testId="settings-logo" /></div></div>
      </Card>

      <Card className="space-y-3 p-6">
        <h3 className="font-heading font-bold">Tournament visibility</h3>
        <label className="flex items-start gap-3" data-testid="settings-public-tournament">
          <Checkbox checked={form.is_public} onCheckedChange={(v) => set("is_public", !!v)} />
          <span>
            <span className="block font-medium">Public tournament</span>
            <span className="text-sm text-muted-foreground">Public tournaments appear in search. Private tournaments remain accessible to anyone with the direct URL.</span>
          </span>
        </label>
      </Card>

      <Card className="space-y-4 p-6">
        <h3 className="font-heading font-bold">Size & format</h3>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {[["max_teams", "Max teams"], ["num_fields", "Fields"], ["min_members", "Min members"], ["max_members", "Max members"], ["match_duration", "Match (min)"], ["break_time", "Break (min)"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Input type="number" min={1} value={form[k]} onChange={(e) => set(k, numberInput(e.target.value))}
              onBlur={() => form[k] === "" && set(k, 1)} className="mt-1.5" data-testid={`settings-${k}`} /></div>
          ))}
        </div>
        {t.tournament_type === "group_elimination" && (
          <div className="grid grid-cols-2 gap-4 border-t pt-4">
            <div>
              <Label>Number of groups</Label>
              <Input type="number" min={2} max={8} value={form.type_config.num_groups ?? 2}
                onChange={(e) => set("type_config", { ...form.type_config, num_groups: numberInput(e.target.value) })}
                onBlur={() => form.type_config.num_groups === "" && set("type_config", { ...form.type_config, num_groups: 2 })}
                className="mt-1.5" data-testid="settings-num-groups" />
            </div>
            <div>
              <Label>Qualify per group</Label>
              <Input type="number" min={1} max={4} value={form.type_config.qualifiers ?? 2}
                onChange={(e) => set("type_config", { ...form.type_config, qualifiers: numberInput(e.target.value) })}
                onBlur={() => form.type_config.qualifiers === "" && set("type_config", { ...form.type_config, qualifiers: 1 })}
                className="mt-1.5" data-testid="settings-qualifiers" />
            </div>
            <p className="col-span-2 text-xs text-muted-foreground">Changing these takes effect the next time you regenerate the structure.</p>
          </div>
        )}
      </Card>

      <Card className="space-y-3 p-6">
        <h3 className="font-heading font-bold">Who can enter scores?</h3>
        {ROLES.map((r) => (
          <label key={r.id} className="flex items-center gap-3" data-testid={`score-role-${r.id}`}>
            <Checkbox checked={form.score_entry.includes(r.id)} onCheckedChange={() => toggleRole(r.id)} />
            <span>{r.label}</span>
          </label>
        ))}
      </Card>

      <Card className="space-y-3 p-6">
        <h3 className="font-heading font-bold">Team captain permissions</h3>
        <label className="flex items-start justify-between gap-4" data-testid="settings-allow-captain-team-rename">
          <span>
            <span className="block font-medium">Allow captains to change their team name</span>
            <span className="text-sm text-muted-foreground">Captains can rename only their own team from the captain page.</span>
          </span>
          <Switch checked={form.allow_captain_team_rename} onCheckedChange={(v) => set("allow_captain_team_rename", !!v)} />
        </label>
        <label className="flex items-start justify-between gap-4 border-t pt-3" data-testid="settings-allow-captain-logo-change">
          <span>
            <span className="block font-medium">Allow captains to change their team logo</span>
            <span className="text-sm text-muted-foreground">Captains can upload or remove their own team logo.</span>
          </span>
          <Switch checked={form.allow_captain_logo_change} onCheckedChange={(v) => set("allow_captain_logo_change", !!v)} />
        </label>
      </Card>

      <Button onClick={save} disabled={busy} data-testid="save-settings-button"><Save className="mr-1.5 h-4 w-4" /> {busy ? "Saving…" : "Save changes"}</Button>

      <Card className="space-y-3 p-6">
        <h3 className="flex items-center gap-2 font-heading font-bold"><Megaphone className="h-4 w-4" /> Announcements</h3>
        <div className="space-y-2">
          <Input value={ann.title} onChange={(e) => setAnn({ ...ann, title: e.target.value })} placeholder="Title" data-testid="ann-title" />
          <Textarea value={ann.body} onChange={(e) => setAnn({ ...ann, body: e.target.value })} placeholder="Message" />
          <Button size="sm" onClick={addAnn} data-testid="add-announcement"><Plus className="mr-1 h-3.5 w-3.5" /> Post</Button>
        </div>
        {(announcements || []).map((a) => (
          <div key={a.id} className="flex items-start justify-between gap-2 rounded-lg border p-3">
            <div><div className="font-semibold">{a.title}</div><div className="text-sm text-muted-foreground">{a.body}</div></div>
            <Button variant="ghost" size="icon" onClick={() => delAnn(a.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
        ))}
      </Card>

      <Card className="space-y-3 p-6">
        <h3 className="font-heading font-bold">Sponsors</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div><Label>Name</Label><Input value={spo.name} onChange={(e) => setSpo({ ...spo, name: e.target.value })} className="mt-1.5" data-testid="sponsor-name" /></div>
          <div><Label>URL</Label><Input value={spo.url} onChange={(e) => setSpo({ ...spo, url: e.target.value })} className="mt-1.5" placeholder="https://example.com" data-testid="sponsor-url" /></div>
          <div className="sm:col-span-2">
            <Label>Logo</Label>
            <div className="mt-1.5"><ImageUpload value={spo.logo_url} onChange={(v) => setSpo({ ...spo, logo_url: v })} testId="sponsor-logo" label="Upload logo" showPreview={false} /></div>
          </div>
          <Button size="sm" className="sm:col-span-2 sm:w-fit" onClick={addSpo} data-testid="add-sponsor"><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
        </div>
        <div className="space-y-2">
          {(sponsors || []).map((s) => (
            <div key={s.id} className="flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
              <div className="min-w-0">
                <div className="font-medium">{s.name}</div>
                {s.url && <div className="truncate text-xs text-muted-foreground">{s.url}</div>}
              </div>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => delSpo(s.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="space-y-3 p-6">
        <h3 className="font-heading font-bold">Tournament status</h3>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setStatus("active")}
            className={user?.vip ? "border-purple-500 bg-purple-50 text-purple-700 hover:bg-purple-100 hover:text-purple-800 dark:bg-purple-950/30 dark:text-purple-300" : ""}
            data-testid="status-active"><Play className="mr-1.5 h-4 w-4" /> Start {user?.vip && <span className="ml-1 rounded bg-purple-600 px-1.5 py-0.5 text-[10px] font-bold text-white">VIP</span>}</Button>
          <Button variant="outline" onClick={() => setStatus("completed")} data-testid="status-completed"><Flag className="mr-1.5 h-4 w-4" /> Finish</Button>
          <Button variant="outline" onClick={() => setStatus("archived")} data-testid="status-archived"><Archive className="mr-1.5 h-4 w-4" /> Archive</Button>
          <Button variant="ghost" onClick={() => setStatus("draft")} data-testid="status-draft">Back to draft</Button>
        </div>
      </Card>

      <Card className="border-destructive/40 p-6">
        <h3 className="font-heading font-bold text-destructive">Danger zone</h3>
        <p className="mb-3 text-sm text-muted-foreground">Permanently delete this tournament and all its data.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild><Button variant="destructive" data-testid="delete-tournament-button"><Trash2 className="mr-1.5 h-4 w-4" /> Delete tournament</Button></AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete this tournament?</AlertDialogTitle><AlertDialogDescription>This cannot be undone. All teams, matches and results will be lost.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">Delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
}
